import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { type PropsWithChildren, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { useTheme, useThemeColors } from "../../../../../contexts";

import { toast } from "sonner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-regular-svg-icons";

import {
  faAt,
  faCircleInfo,
  faGlobe,
  faPalette,
  faPen,
  faPerson,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { Organization, ProfileContactDetails } from "../../../../../client";
import { salesContactState } from "../../../../../state/salesContact";
import { SalesContactDrawerHeader } from "../../../../profile/SalesContactDrawerHeader";
import { LeadRowCard } from "../card/LeadRowCard";
import { ImageUpload } from "../../../../../types";
import { ImageCropperDialog } from "../../../../../components/ImageCropperDialog";
import { updateOrganizationLogo } from "../../../../../requests";
import { organizationInfoState } from "../../../../../state/organizationInfo";
import {
  Button,
  Checkbox,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  Input,
  isEmailOrEmpty,
  isPhoneNumberOrEmpty,
  isString,
  isUrlOrEmpty,
} from "../../../../../components";
import { isURL } from "validator";
import { useParams } from "react-router";
import { useGetOrganizationInfo } from "../../../../../services/organtization/get-organization-info";
import { OrganizationInfoDrawerHeader } from "./OrganizationInfoDrawerHeader";
import { getImageUrl } from "../../../../../utils/image";
import frogLogo from "../../../../../assets/frog_logo.png";
import defaultMainLogo from "../../../../../assets/default-main-logo.png";
import defaultIconLogo from "../../../../../assets/default-icon-logo.png";
import { useUpdateOrganization } from "../../../../../services/organtization/update-organization";
import { cn } from "../../../../../components/utils/cn";
import { queryClient } from "../../../../../utils/query-client";
import { getOrganizationOptions } from "../../../../../services";
import { getOrganizationInfoOptions } from "../../../../../services/organtization/get-organization-info";

interface Props extends PropsWithChildren {}

export const OrganizationInfoDrawer = ({ children }: Props) => {
  const toastId = useRef<string | number>();
  const { updateColors } = useTheme();

  const { primary, secondary } = useThemeColors();

  const { organizationId } = useParams();

  const [email, setEmail] = useState<string>("");
  const [logoAssetId, setLogoAssetId] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [website, setWebsite] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [salesContact, setSalesContact] = useState<string>("");
  const [primaryColor, setPrimaryColor] = useState<string>("");
  const [shortDesc, setShortDesc] = useState<string>("");
  const [secondaryColor, setSecondaryColor] = useState<string>("");
  const [avoidWorkplacePageBreak, setAvoidWorkplacePageBreak] = useState<boolean | null>(null);
  const [avoidWorkplaceDescriptionPageBreak, setAvoidWorkplaceDescriptionPageBreak] = useState<boolean | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string>("");
  const [isIconLogo, setIsIconLogo] = useState<boolean>(false);
  const [originalImageFormat, setOriginalImageFormat] =
    useState<string>("image/png");

  const { showOrganizationInfo } = useSnapshot(organizationInfoState);

  const { data: organizationInfo, refetch: refetchOrgInfo } =
    useGetOrganizationInfo(organizationId ?? "");

  const updateOrgProfile = useUpdateOrganization(
    {
      onSuccess: (data) => {
        // Manually invalidate both organization queries to ensure theme colors update
        if (organizationId) {
          queryClient.invalidateQueries({
            queryKey: getOrganizationOptions(organizationId).queryKey,
          });
          queryClient.invalidateQueries({
            queryKey: getOrganizationInfoOptions(organizationId).queryKey,
          });
        }

        refetchOrgInfo(); // Trigger refetch here
        toast.success("Profile status updated successfully");
      },
    },
    organizationId ?? "",
  );

  if (!organizationId) {
    toast.error("Missing required information");
    return null;
  }

  const handleOrganizationInfoUpdate = (
    updateOrganizationInfo: Organization,
  ) => {
    updateOrgProfile.mutate({
      path: { organizationID: organizationId },
      body: updateOrganizationInfo,
    });
  };

  const handleClose = () => {
    organizationInfoState.showOrganizationInfo = false;
  };

  return (
    <Drawer
      direction="right"
      open={!!showOrganizationInfo}
      onClose={handleClose}
      shouldScaleBackground={false}
    >
      {children}

      <DrawerContent
        className="min-w-[50%] max-w-[50%] flex flex-col pb-8 overflow-y-scroll"
        direction="right"
        data-vaul-no-drag
      >
        <VisuallyHidden.Root>
          <DrawerTitle>Edit Organization</DrawerTitle>
          <DrawerDescription>
            Edit the organization information to fit your brand needs.
          </DrawerDescription>
        </VisuallyHidden.Root>

        <OrganizationInfoDrawerHeader
          handleCreateClick={() => {}}
          title="Edit organization information"
          handleClose={handleClose}
          emailIsValid={true}
        />
        {organizationInfo && (
          <div className="flex flex-col justify-center mt-4 px-8 pb-8">
            <div className="flex flex-col justify-center items-start ">
              <div className="flex flex-row gap-10">
                <div className="cursor-pointer">
                  <h6 className="text-sm text-gray-6">Main logo</h6>
                  <div
                    className="flex justify-center items-center cursor-pointer"
                    onClick={() => {
                      setIsIconLogo(true);
                      document.getElementById("logo-uploader")?.click();
                    }}
                  >
                    <img
                      alt="logo"
                      src={
                        organizationInfo.logoId
                          ? getImageUrl(organizationInfo.logoId)
                          : defaultMainLogo
                      }
                      className={`object-scale-contain h-20`}
                      style={{
                        maxHeight: 100,
                      }}
                    />
                  </div>
                </div>
                <div>
                  <h6 className="text-sm text-gray-6">Icon logo</h6>
                  <div
                    className="flex justify-center items-center rounded-xl cursor-pointer"
                    onClick={() => {
                      setIsIconLogo(false);
                      document.getElementById("logo-uploader")?.click();
                    }}
                  >
                    <img
                      alt="logo"
                      src={
                        organizationInfo.iconLogoId
                          ? getImageUrl(organizationInfo.iconLogoId)
                          : defaultIconLogo
                      }
                      className={`object-scale-contain rounded-xl h-20`}
                      style={{
                        maxHeight: 100,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-8 mt-8">
              <div>
                <h6 className="text-sm text-gray-6">Organization name</h6>
                <Input
                  className=""
                  placeholder="Name"
                  leftIcon={faBuilding}
                  value={companyName || organizationInfo.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCompanyName(e.target.value);
                  }}
                  onBlur={(
                    event: React.FocusEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    const inputValue = event.target.value;
                    handleOrganizationInfoUpdate({
                      ...organizationInfo,
                      name: inputValue,
                    });
                  }}
                  validate={isString}
                />
              </div>

              <div>
                <h6 className="text-sm text-gray-6">Organization website</h6>
                <Input
                  className=""
                  placeholder="Website"
                  leftIcon={faGlobe}
                  value={(website || organizationInfo?.website) ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setWebsite(e.target.value);
                  }}
                  onBlur={(
                    event: React.FocusEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    const inputValue = event.target.value;
                    handleOrganizationInfoUpdate({
                      ...organizationInfo,
                      website: inputValue,
                    });
                  }}
                  validate={isUrlOrEmpty}
                />
              </div>

              <div>
                <h6 className="text-sm text-gray-6">Description</h6>
                <Input
                  className=""
                  placeholder="A few rows about your organization and its mission"
                  leftIcon={faPen}
                  rows={3}
                  value={shortDesc || organizationInfo.shortDesc || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setShortDesc(e.target.value);
                  }}
                  onBlur={(
                    event: React.FocusEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    const inputValue = event.target.value;
                    handleOrganizationInfoUpdate({
                      ...organizationInfo,
                      shortDesc: inputValue,
                    });
                  }}
                  validate={isString}
                />
              </div>

              <div className="flex flex-row justify-between gap-4">
                <div className="w-1/2">
                  <h6 className="text-sm text-gray-6">Primary Color</h6>
                  <div className="flex flex-row gap-1 items-center">
                    <Input
                      className="flex-1"
                      placeholder="Primary color (HEX)"
                      leftIcon={faPalette}
                      value={
                        primaryColor || organizationInfo.primaryColor || ""
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.value && e.target.value.indexOf("#") < 0) {
                          setPrimaryColor("#" + e.target.value);
                        } else {
                          setPrimaryColor(e.target.value);
                        }
                      }}
                      onBlur={(
                        event: React.FocusEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => {
                        const inputValue = event.target.value;
                        var finalColor = inputValue;
                        if (inputValue && inputValue.indexOf("#") < 0) {
                          setPrimaryColor("#" + inputValue);
                          finalColor = "#" + inputValue;
                        } else {
                          setPrimaryColor(inputValue);
                        }
                        handleOrganizationInfoUpdate({
                          ...organizationInfo,
                          primaryColor: finalColor,
                        });
                        updateColors({
                          primary: finalColor,
                        });
                      }}
                      validate={isString}
                    />
                    <div
                      className={`w-12 h-12 rounded-2xl border border-gray-4`}
                      style={{ backgroundColor: primary }}
                    ></div>
                  </div>
                  <LeadRowCard
                    className={cn("text-xs mt-3 w-full")}
                    type="solid"
                  >
                    <div className="flex flex-1 items-center justify-start gap-3">
                      <FontAwesomeIcon
                        width={14}
                        height={14}
                        icon={faCircleInfo}
                        className="w-4 h-4 shrink-0"
                      />
                      <p>The Primary color is used for borders and dividers</p>
                    </div>
                  </LeadRowCard>
                </div>
                <div className="w-1/2">
                  <h6 className="text-sm text-gray-6">Secondary Color</h6>
                  <div className="flex flex-row gap-1 items-center">
                    <Input
                      className="flex-1"
                      placeholder="Secondary color (HEX)"
                      leftIcon={faPalette}
                      value={
                        secondaryColor || organizationInfo.secondaryColor || ""
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.value && e.target.value.indexOf("#") < 0) {
                          setSecondaryColor("#" + e.target.value);
                        } else {
                          setSecondaryColor(e.target.value);
                        }
                      }}
                      onBlur={(
                        event: React.FocusEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => {
                        const inputValue = event.target.value;
                        var finalColor = inputValue;
                        if (inputValue && inputValue.indexOf("#") < 0) {
                          setSecondaryColor("#" + inputValue);
                          finalColor = "#" + inputValue;
                        } else {
                          setSecondaryColor(inputValue);
                        }

                        handleOrganizationInfoUpdate({
                          ...organizationInfo,
                          secondaryColor: finalColor,
                        });
                        updateColors({
                          secondary: finalColor,
                        });
                      }}
                      validate={isString}
                    />
                    <div
                      className={cn(
                        `w-12 h-12 rounded-2xl border border-gray-4`,
                        primary && `bg-[${secondary}]`,
                      )}
                      style={{ backgroundColor: secondary }}
                    ></div>
                  </div>
                  <LeadRowCard
                    className={cn("text-xs mt-3 w-full")}
                    type="solid"
                  >
                    <div className="flex flex-1 items-center justify-start gap-3">
                      <FontAwesomeIcon
                        width={14}
                        height={14}
                        icon={faCircleInfo}
                        className="w-4 h-4 shrink-0"
                      />
                      <p>The Secondary color is used for titles</p>
                    </div>
                  </LeadRowCard>
                </div>
              </div>

              <div className="mt-6">
                <h6 className="text-sm text-gray-6 mb-2">PDF settings</h6>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={avoidWorkplacePageBreak ?? organizationInfo?.avoidWorkplacePageBreak ?? false}
                      onCheckedChange={(checked) => {
                        const value = checked === true;
                        setAvoidWorkplacePageBreak(value);
                        if (!value) {
                          setAvoidWorkplaceDescriptionPageBreak(false);
                        }
                        handleOrganizationInfoUpdate({
                          ...organizationInfo,
                          avoidWorkplacePageBreak: value,
                          avoidWorkplaceDescriptionPageBreak: value ? (avoidWorkplaceDescriptionPageBreak ?? organizationInfo?.avoidWorkplaceDescriptionPageBreak ?? false) : false,
                        });
                      }}
                    />
                    <label className="text-sm">Smart page break</label>
                  </div>
                  <div className={cn("flex items-center gap-3", !(avoidWorkplacePageBreak ?? organizationInfo?.avoidWorkplacePageBreak) && "opacity-40 pointer-events-none")}>
                    <Checkbox
                      checked={avoidWorkplaceDescriptionPageBreak ?? organizationInfo?.avoidWorkplaceDescriptionPageBreak ?? false}
                      disabled={!(avoidWorkplacePageBreak ?? organizationInfo?.avoidWorkplacePageBreak)}
                      onCheckedChange={(checked) => {
                        const value = checked === true;
                        setAvoidWorkplaceDescriptionPageBreak(value);
                        handleOrganizationInfoUpdate({
                          ...organizationInfo,
                          avoidWorkplaceDescriptionPageBreak: value,
                        });
                      }}
                    />
                    <label className="text-sm">Do not break work experience entries</label>
                  </div>
                </div>
              </div>
            </div>
            <input
              className="hidden"
              type="file"
              id={"logo-uploader"}
              onChange={(event) => {
                if (
                  event.target.files === null ||
                  event.target.files.length === 0
                )
                  return;

                const file = event.target.files[0];
                if (!file) return;

                const reader = new FileReader();

                reader.onload = (e: ProgressEvent<FileReader>) => {
                  const formatInfo = (e.target?.result as string).split(",")[0];
                  const base64String = (e.target?.result as string).split(
                    ",",
                  )[1];
                  const format = formatInfo.split(";")[0].split(":")[1];

                  const imageUpload: ImageUpload = {
                    fileType: formatInfo,
                    base64File: base64String,
                  };

                  // Store the original format to preserve it after cropping
                  setOriginalImageFormat(format);
                  setImageToCrop(imageUpload.base64File);
                  setShowCropper(true);
                };

                reader.readAsDataURL(file);
              }}
            ></input>
            {showCropper && (
              <ImageCropperDialog
                open={showCropper}
                onClose={() => setShowCropper(false)}
                image={imageToCrop}
                imageFormat={originalImageFormat}
                onCrop={(croppedImage: string) => {
                  const imageUpload: ImageUpload = {
                    fileType: originalImageFormat,
                    base64File: croppedImage.split(",")[1],
                  };

                  updateOrganizationLogo(
                    organizationId,
                    imageUpload,
                    isIconLogo,
                  ).then((newOrganizationLogoId: string) => {
                    // Invalidate the query to refresh the data
                    queryClient.invalidateQueries({
                      queryKey: getOrganizationOptions(organizationId ?? "")
                        .queryKey,
                    });
                    refetchOrgInfo();
                  });
                }}
                aspectRatio={1}
              />
            )}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
